import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { useAdminAuth } from "./AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { 
  Users, 
  Mail, 
  Crown, 
  Sparkles, 
  Download, 
  Trash2, 
  Search,
  Loader2,
  UserCheck,
  Clock,
  CreditCard,
  ChevronLeft,
  ChevronRight,
  ShieldOff,
  Sparkles as SparklesIcon,
  AlertTriangle,
  Eye,
  Wand2
} from "lucide-react";
import { toast } from "sonner";

export default function AdminCustomers() {
  const { token, loading: authLoading } = useAdminAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [search, setSearch] = useState("");
  const [source, setSource] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all"); // all | subscribers | non_subscribers | founders | unsubscribed
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revoking, setRevoking] = useState(false);
  const [diagnosticTarget, setDiagnosticTarget] = useState(null);
  const [diagnostic, setDiagnostic] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [bulkCleaning, setBulkCleaning] = useState(false);
  const [bulkCleanResult, setBulkCleanResult] = useState(null);
  const [confirmBulkClean, setConfirmBulkClean] = useState(false);
  const [stripeMode, setStripeMode] = useState(null);
  const [stripeKeyValid, setStripeKeyValid] = useState(null);
  const [stripeKeyError, setStripeKeyError] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Checkout failures — users who tried to subscribe but hit a Stripe error.
  // These are the highest-priority rescue candidates.
  const [failuresData, setFailuresData] = useState(null);
  const [failuresLoading, setFailuresLoading] = useState(false);
  const [showFailuresModal, setShowFailuresModal] = useState(false);

  // Email Lookup (searches every signup list, not just the visible Customers table)
  const [lookupQuery, setLookupQuery] = useState("");
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupResults, setLookupResults] = useState(null);

  // Browse-by-source (for when you don't remember the email)
  const [browseSources, setBrowseSources] = useState([]);
  const [browseSource, setBrowseSource] = useState("");
  const [browseFilter, setBrowseFilter] = useState("");
  const [browseLoading, setBrowseLoading] = useState(false);
  const [browseData, setBrowseData] = useState(null);
  const [browsePage, setBrowsePage] = useState(1);

  useEffect(() => {
    // Only fetch when auth is ready and we have a token
    if (!authLoading && token) {
      fetchCustomers();
      // Independent stripe-mode check so the banner can show even if customer fetch fails
      axios
        .get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => {
          setStripeMode(r.data?.stripe_mode || null);
          setStripeKeyValid(r.data?.stripe_key_valid);
          setStripeKeyError(r.data?.stripe_key_error || null);
        })
        .catch(() => {});

      // Fetch checkout failures — always run once per page load so the
      // "N lost signups" indicator is fresh.
      axios
        .get(`${API}/admin/checkout-failures`, {
          params: { page: 1, limit: 500 },
          headers: { Authorization: `Bearer ${token}` },
        })
        .then(r => setFailuresData(r.data))
        .catch(() => {});
    } else if (!authLoading && !token) {
      setLoading(false);
    }
  }, [source, page, token, authLoading]);

  const fetchCustomers = async (searchQuery = search) => {
    // Guard: don't fetch without token
    if (!token) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/customers`, {
        params: { source, search: searchQuery, page, limit: 20 },
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data.customers || []);
      setStats(response.data.stats || {});
      setTotalPages(response.data.pages || 1);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error("Failed to fetch customers:", error);
      toast.error("Failed to load customers");
      setCustomers([]);
      setStats({});
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchCustomers(search);
  };

  const refreshFailures = async () => {
    setFailuresLoading(true);
    try {
      const r = await axios.get(`${API}/admin/checkout-failures`, {
        params: { page: 1, limit: 500 },
        headers: { Authorization: `Bearer ${token}` },
      });
      setFailuresData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load checkout failures");
    } finally {
      setFailuresLoading(false);
    }
  };

  const copyFailedEmails = async () => {
    const emails = (failuresData?.rows || []).map(r => r.email).filter(Boolean);
    if (!emails.length) {
      toast.info("No failed-checkout emails to copy");
      return;
    }
    try {
      await navigator.clipboard.writeText(emails.join(", "));
      toast.success(`Copied ${emails.length} email${emails.length === 1 ? "" : "s"} to clipboard — paste into Broadcast → Recipients`);
    } catch {
      toast.error("Could not copy — select the list manually");
    }
  };

  const markAllRecovered = async () => {
    const emails = (failuresData?.rows || []).map(r => r.email).filter(Boolean);
    if (!emails.length) return;
    if (!window.confirm(`Mark all ${emails.length} failed-checkout users as recovered? (Do this AFTER you've sent them a rescue email.)`)) return;
    try {
      const r = await axios.post(
        `${API}/admin/checkout-failures/mark-recovered`,
        { emails },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Marked ${r.data?.updated || 0} as recovered`);
      refreshFailures();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to mark recovered");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    
    try {
      const res = await axios.delete(`${API}/admin/customers/${deleteTarget.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data?.message || "Customer hidden — you can restore them anytime from Email Lookup");
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to hide customer");
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/admin/customers/export`, {
        params: { source },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `customers_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Export downloaded successfully");
    } catch (error) {
      toast.error("Failed to export customers");
    } finally {
      setExporting(false);
    }
  };

  const handleRevokeSubscriber = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      const res = await axios.post(
        `${API}/admin/customers/${revokeTarget.id}/revoke-subscription`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const subs = res.data?.subscriptions_demoted ?? 0;
      const flags = res.data?.user_flags_cleared ?? 0;
      toast.success(
        subs || flags
          ? `Revoked: ${subs} subscription${subs === 1 ? "" : "s"} demoted, founder flag cleared.`
          : "Subscriber status already clean — nothing to revoke."
      );
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to revoke subscriber status");
    } finally {
      setRevoking(false);
      setRevokeTarget(null);
    }
  };

  const openDiagnostic = async (customer) => {
    setDiagnosticTarget(customer);
    setDiagnostic(null);
    setDiagLoading(true);
    try {
      const res = await axios.get(
        `${API}/admin/customers/${customer.id}/diagnostic`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDiagnostic(res.data);
    } catch (error) {
      toast.error("Failed to load diagnostic");
      setDiagnosticTarget(null);
    } finally {
      setDiagLoading(false);
    }
  };

  const handleBulkClean = async () => {
    setBulkCleaning(true);
    setBulkCleanResult(null);
    try {
      const res = await axios.post(
        `${API}/admin/cleanup-unpaid-subscribers`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBulkCleanResult(res.data);
      toast.success(res.data?.message || "Cleanup complete");
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Bulk cleanup failed");
    } finally {
      setBulkCleaning(false);
      setConfirmBulkClean(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const handleEmailLookup = async (e) => {
    if (e) e.preventDefault();
    const q = lookupQuery.trim();
    if (q.length < 2) {
      toast.error("Type at least 2 characters to search");
      return;
    }
    setLookupLoading(true);
    try {
      const res = await axios.get(`${API}/admin/email-lookup`, {
        params: { q },
        headers: { Authorization: `Bearer ${token}` }
      });
      setLookupResults(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Lookup failed");
      setLookupResults(null);
    } finally {
      setLookupLoading(false);
    }
  };

  const clearLookup = () => {
    setLookupQuery("");
    setLookupResults(null);
  };

  const handleRestore = async (group) => {
    try {
      const res = await axios.post(
        `${API}/admin/customers/restore`,
        { email: group.email },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data?.message || "Restored");
      // Re-run the same lookup so the badges update
      handleEmailLookup();
      // Also refresh the main customers table since the row is back
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Restore failed");
    }
  };

  // Load the list of available sources whenever the admin opens the page
  useEffect(() => {
    if (!authLoading && token) {
      axios.get(`${API}/admin/email-sources`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setBrowseSources(r.data?.sources || []))
        .catch(() => setBrowseSources([]));
    }
  }, [token, authLoading]);

  const handleBrowseSource = async (sourceKey, pageNum = 1) => {
    if (!sourceKey) {
      setBrowseData(null);
      setBrowseSource("");
      return;
    }
    setBrowseSource(sourceKey);
    setBrowsePage(pageNum);
    setBrowseLoading(true);
    try {
      const res = await axios.get(`${API}/admin/email-source-browse`, {
        params: { source: sourceKey, page: pageNum, limit: 50 },
        headers: { Authorization: `Bearer ${token}` }
      });
      setBrowseData(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load list");
      setBrowseData(null);
    } finally {
      setBrowseLoading(false);
    }
  };

  const handleRestoreFromBrowse = async (email) => {
    try {
      const res = await axios.post(
        `${API}/admin/customers/restore`,
        { email },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data?.message || "Restored");
      handleBrowseSource(browseSource, browsePage);
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Restore failed");
    }
  };

  // Show loading spinner while auth is being checked
  if (authLoading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="admin-customers-loading">
        <Loader2 className="h-8 w-8 animate-spin text-gold" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-customers-page">
      {/* Stripe-mode warning banner — visible whenever the backend is using a
          test-mode Stripe key. This is the #1 cause of "fake subscriber"
          confusion: test-mode checkouts complete successfully but never
          charge anyone. */}
      {stripeKeyValid === false && (
        <div
          className="rounded-lg border-2 border-red-600 bg-red-100 p-4 flex items-start gap-3"
          data-testid="stripe-key-invalid-banner"
        >
          <AlertTriangle className="h-6 w-6 text-red-700 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">
            <p className="font-bold text-red-800 text-base">
              🚨 Stripe API key is INVALID — every checkout is failing silently
            </p>
            <p className="text-red-800 mt-1.5">
              New signups are landing on the site, clicking Subscribe, and getting an
              error from Stripe. They end up as free accounts, and neither a subscription
              nor a payment record is created. This is why recent customers all show as
              &quot;Free&quot; in this list.
            </p>
            {stripeKeyError && (
              <p className="text-red-800 mt-2 font-mono text-xs bg-red-50 border border-red-200 rounded px-2 py-1">
                Stripe error: {stripeKeyError}
              </p>
            )}
            <p className="text-red-800 mt-2">
              <strong>To fix:</strong> Go to your Emergent app → <strong>Deploy</strong> →
              <strong> Secrets</strong> → find <code className="bg-red-200 px-1 mx-1 rounded">STRIPE_API_KEY</code> and
              paste your live Stripe secret key (it must start with
              <code className="bg-red-200 px-1 mx-1 rounded">sk_live_</code>).
              Get it from your Stripe dashboard → Developers → API keys, with the
              Test/Live toggle set to <strong>Live</strong>. Then click{" "}
              <strong>Redeploy</strong> so production picks up the new value.
            </p>
          </div>
        </div>
      )}
      {stripeMode === "test" && (() => {
        // Distinguish preview (test-mode is intentional, informational) from
        // production (test-mode = the live key got replaced, urgent).
        const isPreview =
          typeof window !== "undefined" &&
          /\.preview\.emergentagent\.com$|\.preview\.|localhost/.test(window.location.hostname);
        if (isPreview) {
          return (
            <div
              className="rounded-lg border border-blue-300 bg-blue-50 p-3 flex items-start gap-3"
              data-testid="stripe-test-mode-banner"
            >
              <AlertTriangle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 text-sm text-blue-900">
                <p className="font-semibold">Preview is using a Stripe TEST key</p>
                <p className="mt-1">
                  This is intentional — preview is your dev sandbox, so any
                  &quot;subscribe&quot; clicks here go to Stripe&apos;s test checkout
                  and never charge a real card. Production is unaffected and continues
                  to use your live key.
                </p>
              </div>
            </div>
          );
        }
        return (
          <div
            className="rounded-lg border-2 border-red-500 bg-red-50 p-4 flex items-start gap-3"
            data-testid="stripe-test-mode-banner"
          >
            <AlertTriangle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
              <p className="font-bold text-red-700 text-base">
                ⚠️ Stripe is in TEST mode — no real payments are being collected
              </p>
              <p className="text-red-700 mt-1.5">
                Anyone who &quot;subscribes&quot; right now is going through
                Stripe&apos;s test checkout. They can use fake card numbers and
                will be marked as paid in this dashboard, but no money is ever
                charged.
              </p>
              <p className="text-red-700 mt-2">
                <strong>To fix:</strong> Contact Emergent Support and ask them to update the
                <code className="bg-red-100 px-1 mx-1 rounded">STRIPE_API_KEY</code>
                environment variable on your production deployment to your
                <code className="bg-red-100 px-1 mx-1 rounded">sk_live_…</code>
                key from your Stripe dashboard (Developers → API keys, with the Test/Live
                toggle set to <strong>Live</strong>).
              </p>
            </div>
          </div>
        );
      })()}

      {/* Checkout failures — highest-priority rescue candidates */}
      {failuresData?.total > 0 && (
        <div
          className="rounded-lg border-2 border-amber-500 bg-amber-50 p-4"
          data-testid="checkout-failures-card"
        >
          <div className="flex items-start gap-3 flex-wrap">
            <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-[250px]">
              <p className="font-bold text-amber-800 text-base">
                {failuresData.total} {failuresData.total === 1 ? "person" : "people"} tried to subscribe but hit a Stripe error
              </p>
              <p className="text-amber-800 text-sm mt-1.5">
                These are your highest-value rescue candidates — they were literally
                on the checkout page. Once Stripe is fixed, email them a
                &quot;we&apos;re back, sorry about that&quot; note with a promo code.
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={() => setShowFailuresModal(true)}
                variant="outline"
                size="sm"
                className="border-amber-500 text-amber-700 hover:bg-amber-100"
                data-testid="view-checkout-failures-btn"
              >
                View list
              </Button>
              <Button
                onClick={copyFailedEmails}
                variant="outline"
                size="sm"
                className="border-amber-500 text-amber-700 hover:bg-amber-100"
                data-testid="copy-failed-emails-btn"
              >
                Copy emails
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center">
        <div>
          <h1 className="font-serif text-3xl font-bold">Customers</h1>
          <p className="text-muted-foreground">
            Manage all registered users and waitlist signups
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => setConfirmBulkClean(true)}
            variant="outline"
            className="border-amber-500 text-amber-600 hover:bg-amber-50"
            data-testid="bulk-clean-unpaid-btn"
            title="One-click revoke for every user flagged as a subscriber/founder without payment proof"
          >
            <Wand2 className="h-4 w-4 mr-2" />
            Clean up unpaid "subscribers"
          </Button>
          <Button
            onClick={handleExport}
            disabled={exporting}
            className="bg-gold text-black hover:bg-gold/90"
            data-testid="export-csv-btn"
          >
            {exporting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            Export CSV
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <Users className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total_registered || 0}</p>
                <p className="text-xs text-muted-foreground">Registered Users</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <Mail className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total_waitlist || 0}</p>
                <p className="text-xs text-muted-foreground">Waitlist Signups</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/20 rounded-lg">
                <Sparkles className="h-5 w-5 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total_founding_members || 0}</p>
                <p className="text-xs text-muted-foreground">Founding Members</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <CreditCard className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total_subscribed || 0}</p>
                <p className="text-xs text-muted-foreground">Active Subscribers</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-muted rounded-lg">
                <ShieldOff className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <p className="text-2xl font-bold" data-testid="unsubscribed-count">{stats.total_unsubscribed || 0}</p>
                <p className="text-xs text-muted-foreground">Unsubscribed</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Email Lookup — searches every signup list, even records no longer in the visible Customers table */}
      <Card className="border-purple-300 bg-purple-50/40" data-testid="email-lookup-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Search className="h-5 w-5 text-purple-600" />
            Find any email (even if deleted)
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Searches every list at once: registered accounts, waitlist, free-song downloads, promo subscribers, sent broadcasts, and Stripe checkouts. Use this when an email seems to be missing from the table below.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleEmailLookup} className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Type any part of an email — e.g. sarah, @gmail.com, lawschool"
                value={lookupQuery}
                onChange={(e) => setLookupQuery(e.target.value)}
                className="pl-10"
                data-testid="email-lookup-input"
              />
            </div>
            <Button
              type="submit"
              disabled={lookupLoading || lookupQuery.trim().length < 2}
              className="bg-purple-600 text-white hover:bg-purple-700"
              data-testid="email-lookup-submit"
            >
              {lookupLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
              Search everywhere
            </Button>
            {lookupResults && (
              <Button type="button" variant="ghost" onClick={clearLookup} data-testid="email-lookup-clear">
                Clear
              </Button>
            )}
          </form>

          {lookupResults && (
            <div className="mt-4" data-testid="email-lookup-results">
              {lookupResults.total_unique_emails === 0 ? (
                <div className="text-sm text-muted-foreground bg-white border rounded-md p-4">
                  No matches found for <span className="font-semibold">&ldquo;{lookupResults.query}&rdquo;</span> in any list.
                  <br />
                  If you typed a full email, try a shorter portion (e.g. just the part before the @).
                </div>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground mb-2">
                    Found <span className="font-semibold text-foreground">{lookupResults.total_unique_emails}</span> unique email{lookupResults.total_unique_emails !== 1 ? 's' : ''} across <span className="font-semibold text-foreground">{lookupResults.total_records}</span> record{lookupResults.total_records !== 1 ? 's' : ''}.
                  </p>
                  <div className="space-y-3 max-h-[480px] overflow-y-auto pr-1">
                    {lookupResults.results.map((g) => (
                      <div key={g.email} className="bg-white border rounded-md p-3" data-testid="email-lookup-result-row">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-sm font-semibold break-all">{g.email}</span>
                            {g.any_deleted && (
                              <Badge className="bg-red-100 text-red-800 border-red-200" variant="outline">
                                Deleted
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary" className="text-xs">
                              in {g.places_count} list{g.places_count !== 1 ? 's' : ''}
                            </Badge>
                            {g.restorable && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleRestore(g)}
                                className="h-7 text-xs border-green-400 text-green-700 hover:bg-green-50"
                                data-testid="email-lookup-restore-btn"
                              >
                                Restore
                              </Button>
                            )}
                          </div>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          First seen: {formatDate(g.first_seen)} · Last activity: {formatDate(g.last_seen)}
                        </div>
                        <div className="mt-2 grid gap-1.5">
                          {g.records.map((r, idx) => (
                            <div key={idx} className="flex items-start gap-2 text-xs">
                              <Badge
                                className={
                                  r.source === "registered" ? "bg-blue-100 text-blue-800 border-blue-200" :
                                  r.source === "waitlist"   ? "bg-purple-100 text-purple-800 border-purple-200" :
                                  r.source === "lead"       ? "bg-amber-100 text-amber-800 border-amber-200" :
                                  r.source === "promo_subscriber" ? "bg-pink-100 text-pink-800 border-pink-200" :
                                  r.source === "broadcast"  ? "bg-green-100 text-green-800 border-green-200" :
                                  r.source === "payment"    ? "bg-emerald-100 text-emerald-800 border-emerald-200" :
                                  "bg-muted text-muted-foreground"
                                }
                                variant="outline"
                              >
                                {r.source_label}
                              </Badge>
                              <div className="flex-1">
                                <span className="text-muted-foreground">{formatDate(r.date)}</span>
                                {r.context && <span className="ml-2">· {r.context}</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Browse-by-source: for when you don't remember the email */}
          <div className="mt-6 pt-5 border-t border-purple-200">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <div>
                <h3 className="font-semibold text-sm">Don&rsquo;t remember the email? Browse a list instead</h3>
                <p className="text-xs text-muted-foreground">Pick any list below to see every email captured in it — scroll until you recognize the one you want.</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Select value={browseSource} onValueChange={(v) => handleBrowseSource(v, 1)}>
                <SelectTrigger className="w-full sm:w-[420px]" data-testid="email-source-picker">
                  <SelectValue placeholder="Pick a list to browse..." />
                </SelectTrigger>
                <SelectContent>
                  {browseSources.map((s) => (
                    <SelectItem key={s.key} value={s.key} disabled={s.count === 0}>
                      {s.label} <span className="text-muted-foreground ml-1">({s.count})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {browseData && (
                <Input
                  placeholder="Filter visible emails..."
                  value={browseFilter}
                  onChange={(e) => setBrowseFilter(e.target.value)}
                  className="w-full sm:w-[260px]"
                  data-testid="email-browse-filter"
                />
              )}
            </div>

            {browseLoading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
              </div>
            )}

            {!browseLoading && browseData && (
              <div className="mt-3" data-testid="email-browse-results">
                <p className="text-sm text-muted-foreground mb-2">
                  Showing {browseData.rows.length} of <span className="font-semibold text-foreground">{browseData.total}</span> total
                  {browseData.pages > 1 && ` · Page ${browseData.page} of ${browseData.pages}`}
                </p>
                <div className="space-y-1.5 max-h-[480px] overflow-y-auto pr-1 bg-white border rounded-md p-2">
                  {browseData.rows
                    .filter(r => !browseFilter || (r.email || "").toLowerCase().includes(browseFilter.toLowerCase()))
                    .map((r, idx) => (
                    <div key={`${r.email}-${idx}`} className="flex items-center justify-between gap-2 py-1.5 px-2 hover:bg-purple-50 rounded text-sm">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className="font-mono text-xs sm:text-sm truncate">{r.email}</span>
                        {r.deleted_at && (
                          <Badge className="bg-red-100 text-red-800 border-red-200 text-[10px]" variant="outline">
                            Deleted
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-xs text-muted-foreground hidden md:inline">{formatDate(r.date)}</span>
                        {r.context && <span className="text-xs text-muted-foreground hidden lg:inline">· {r.context}</span>}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 text-[11px] px-2"
                          onClick={() => { setLookupQuery(r.email); handleEmailLookup({preventDefault: ()=>{}}); }}
                          data-testid="email-browse-inspect-btn"
                        >
                          Inspect
                        </Button>
                        {r.restorable && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 text-[11px] px-2 border-green-400 text-green-700 hover:bg-green-50"
                            onClick={() => handleRestoreFromBrowse(r.email)}
                            data-testid="email-browse-restore-btn"
                          >
                            Restore
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                  {browseData.rows.length === 0 && (
                    <div className="text-sm text-muted-foreground p-3">This list is empty.</div>
                  )}
                </div>
                {browseData.pages > 1 && (
                  <div className="flex items-center justify-between mt-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={browsePage <= 1}
                      onClick={() => handleBrowseSource(browseSource, browsePage - 1)}
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" /> Previous
                    </Button>
                    <span className="text-xs text-muted-foreground">Page {browseData.page} of {browseData.pages}</span>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={browsePage >= browseData.pages}
                      onClick={() => handleBrowseSource(browseSource, browsePage + 1)}
                    >
                      Next <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by email or name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="customer-search-input"
                />
              </div>
              <Button type="submit" variant="outline">Search</Button>
            </form>
            
            <Select value={source} onValueChange={(v) => { setSource(v); setPage(1); }}>
              <SelectTrigger className="w-[180px]" data-testid="source-filter">
                <SelectValue placeholder="Filter by source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Customers</SelectItem>
                <SelectItem value="users">Registered Only</SelectItem>
                <SelectItem value="waitlist">Waitlist Only</SelectItem>
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[200px]" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="subscribers">Subscribers only</SelectItem>
                <SelectItem value="non_subscribers">Non-subscribers</SelectItem>
                <SelectItem value="founders">Founders</SelectItem>
                <SelectItem value="unsubscribed">Unsubscribed only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Customers Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {total} Customer{total !== 1 ? 's' : ''} Found
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-gold" />
            </div>
          ) : (() => {
              // Apply client-side status filter on top of the source-filtered server response
              const filtered = customers.filter((c) => {
                switch (statusFilter) {
                  case "subscribers":     return !!c.has_subscription;
                  case "non_subscribers": return !c.has_subscription;
                  case "founders":        return !!c.is_founding_member;
                  case "unsubscribed":    return !!c.is_unsubscribed;
                  default:                return true;
                }
              });
              if (filtered.length === 0) {
                return (
                  <div className="text-center py-12 text-muted-foreground" data-testid="empty-list">
                    No customers match this filter
                  </div>
                );
              }
              return (
                <>
                  {statusFilter !== "all" && (
                    <p className="text-xs text-muted-foreground mb-3" data-testid="filter-summary">
                      Showing {filtered.length} of {customers.length} on this page · filter: <strong>{statusFilter.replace(/_/g, " ")}</strong>
                    </p>
                  )}
                  <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Signup Date</TableHead>
                    <TableHead>Subscriber Since</TableHead>
                    <TableHead className="text-center">Logins</TableHead>
                    <TableHead>Last login</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((customer) => (
                    <TableRow key={customer.id} data-testid={`customer-row-${customer.id}`}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span>{customer.email}</span>
                          {customer.is_unsubscribed && (
                            <Badge
                              className="bg-muted text-muted-foreground border-muted text-[10px] px-1.5 py-0"
                              title="This contact unsubscribed from email — emails to them will be skipped"
                              data-testid={`unsubscribed-tag-${customer.id}`}
                            >
                              <ShieldOff className="h-3 w-3 mr-1" />
                              Unsubscribed
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{customer.name || "-"}</TableCell>
                      <TableCell>
                        <Badge variant={customer.source === "registered" ? "default" : "outline"}>
                          {customer.source === "registered" ? (
                            <><UserCheck className="h-3 w-3 mr-1" /> Registered</>
                          ) : (
                            <><Clock className="h-3 w-3 mr-1" /> Waitlist</>
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDate(customer.created_at)}</TableCell>
                      <TableCell data-testid={`subscriber-since-${customer.id}`}>
                        {customer.subscribed_since ? (
                          <span className="text-green-500 font-medium">{formatDate(customer.subscribed_since)}</span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center" data-testid={`login-count-${customer.id}`}>
                        {customer.source === "registered" ? (
                          <span
                            className={
                              "inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded-full text-sm font-semibold " +
                              ((customer.login_count || 0) === 0
                                ? "bg-muted text-muted-foreground"
                                : (customer.login_count || 0) >= 10
                                  ? "bg-purple-100 text-purple-800 border border-purple-200"
                                  : "bg-blue-50 text-blue-700 border border-blue-200")
                            }
                            title={`${customer.login_count || 0} successful login${customer.login_count === 1 ? "" : "s"}`}
                          >
                            {customer.login_count || 0}
                          </span>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </TableCell>
                      <TableCell data-testid={`last-login-${customer.id}`}>
                        {customer.last_login_at ? (
                          <span
                            className="text-xs"
                            title={
                              customer.last_login_inferred
                                ? "Inferred from last song play (before login tracking was added)"
                                : "Last successful login"
                            }
                          >
                            {formatDate(customer.last_login_at)}
                            {customer.last_login_inferred && (
                              <span className="text-[10px] text-muted-foreground italic ml-1">~</span>
                            )}
                          </span>
                        ) : (
                          <span className="text-muted-foreground text-xs">
                            {customer.source === "registered" ? "Never" : "—"}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {customer.is_founding_member && (
                            <Badge className="bg-gradient-to-r from-amber-500 to-yellow-500 text-black border-0 text-xs">
                              <Sparkles className="h-3 w-3 mr-1" />
                              Founder
                            </Badge>
                          )}
                          {customer.has_subscription && (
                            <Badge className="bg-green-500/20 text-green-500 border-green-500/30 text-xs">
                              <Crown className="h-3 w-3 mr-1" />
                              Subscribed
                            </Badge>
                          )}
                          {!customer.is_founding_member && !customer.has_subscription && customer.source === "registered" && (
                            <Badge variant="outline" className="text-xs">Free</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {customer.source === "registered" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-muted-foreground hover:text-primary"
                              onClick={() => openDiagnostic(customer)}
                              title="Inspect: see exactly what data we have on this customer"
                              data-testid={`diagnostic-customer-${customer.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          )}
                          {customer.source === "registered" && (customer.is_founding_member || customer.has_subscription) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-amber-600 hover:text-amber-700 hover:bg-amber-500/10"
                              onClick={() => setRevokeTarget(customer)}
                              title="Revoke subscriber status (keeps customer record)"
                              data-testid={`revoke-customer-${customer.id}`}
                            >
                              <ShieldOff className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                            onClick={() => setDeleteTarget(customer)}
                            title="Delete customer entirely"
                            data-testid={`delete-customer-${customer.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    Page {page} of {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" /> Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      Next <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </>
              );
            })()}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hide this customer?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{deleteTarget?.email}</strong> will be removed from this list,
              but the email is <strong>kept on file</strong> so you can recover it later.
              <span className="block mt-2 text-sm text-muted-foreground">
                You can find &amp; restore it any time using the <strong>&ldquo;Find any email&rdquo;</strong> search at the top of this page.
              </span>
              {deleteTarget?.source === "registered" && (
                <span className="block mt-2 text-amber-600 text-sm">
                  Any active subscription will be revoked. Payment history is preserved.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              Hide customer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Revoke Subscriber Status Confirmation Dialog */}
      <AlertDialog open={!!revokeTarget} onOpenChange={() => !revoking && setRevokeTarget(null)}>
        <AlertDialogContent data-testid="revoke-subscriber-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke subscriber status?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{revokeTarget?.email}</strong> will no longer show as a Founding Member
              or Subscriber. We&apos;ll keep their customer record intact (they stay in your
              marketing list).
              <span className="block mt-3 text-amber-600 text-xs">
                Use this for accounts that started checkout but never paid. This only affects
                unpaid subscriptions — real paid subs (with a confirmed Stripe payment) are
                left alone and must be canceled via Stripe.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revoking}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRevokeSubscriber}
              disabled={revoking}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="confirm-revoke-subscriber"
            >
              {revoking ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Revoking…</>
              ) : (
                <><ShieldOff className="h-4 w-4 mr-2" /> Revoke status</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Diagnostic Dialog — shows what our DB has on a single customer */}
      <Dialog open={!!diagnosticTarget} onOpenChange={(o) => { if (!o) { setDiagnosticTarget(null); setDiagnostic(null); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="diagnostic-dialog">
          <DialogHeader>
            <DialogTitle>Customer diagnostic</DialogTitle>
            <DialogDescription>
              Everything our database has on this customer. Cross-reference against Stripe
              to confirm whether they actually paid.
            </DialogDescription>
          </DialogHeader>
          {diagLoading || !diagnostic ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : (
            <div className="space-y-4 text-sm">
              {/* Verdict banner */}
              <div className={`p-3 rounded-lg border ${
                diagnostic.verdict?.is_real_paid_subscriber
                  ? "bg-green-500/10 border-green-500/30 text-green-700"
                  : "bg-amber-500/10 border-amber-500/30 text-amber-700"
              }`}>
                <p className="font-semibold flex items-center gap-2">
                  {diagnostic.verdict?.is_real_paid_subscriber ? (
                    <><UserCheck className="h-4 w-4" /> Real paid subscriber</>
                  ) : (
                    <><AlertTriangle className="h-4 w-4" /> No payment proof in our DB</>
                  )}
                </p>
                <p className="text-xs mt-1">{diagnostic.verdict?.summary}</p>
              </div>

              {/* User */}
              <div>
                <h3 className="font-semibold mb-1">User</h3>
                <div className="bg-secondary/40 rounded p-3 space-y-1 text-xs font-mono">
                  <div>email: <strong>{diagnostic.user?.email}</strong></div>
                  <div>user_id: {diagnostic.user?.user_id}</div>
                  <div>created: {diagnostic.user?.created_at}</div>
                  <div className={diagnostic.user?.is_founding_member ? "text-amber-600 font-bold" : ""}>
                    is_founding_member: {String(diagnostic.user?.is_founding_member || false)}
                  </div>
                  {diagnostic.user?.founding_member_since && (
                    <div>founding_member_since: {diagnostic.user.founding_member_since}</div>
                  )}
                </div>
              </div>

              {/* Subscriptions */}
              <div>
                <h3 className="font-semibold mb-1">Subscriptions ({diagnostic.subscription_count})</h3>
                {diagnostic.subscriptions?.length ? (
                  <div className="space-y-2">
                    {diagnostic.subscriptions.map((s, i) => (
                      <div key={i} className="bg-secondary/40 rounded p-3 text-xs font-mono space-y-0.5">
                        <div>plan: <strong>{s.plan_id}</strong></div>
                        <div className={
                          s.status === "active" || s.status === "trialing"
                            ? (s.paid_at || s.stripe_payment_status === "paid" ? "text-green-600 font-bold" : "text-amber-600 font-bold")
                            : "text-muted-foreground"
                        }>status: {s.status}</div>
                        <div>paid_at: {s.paid_at || <span className="text-amber-600">(none)</span>}</div>
                        <div>stripe_payment_status: {s.stripe_payment_status || <span className="text-muted-foreground">(none)</span>}</div>
                        <div>stripe_subscription_id: {s.stripe_subscription_id || <span className="text-muted-foreground">(none)</span>}</div>
                        <div>stripe_session_id: {s.stripe_session_id || <span className="text-muted-foreground">(none)</span>}</div>
                        <div>stripe_customer_id: {s.stripe_customer_id || <span className="text-muted-foreground">(none)</span>}</div>
                        <div>created_at: {s.created_at}</div>
                        {s.demoted_at && (
                          <div className="text-amber-600">demoted_at: {s.demoted_at} ({s.demoted_reason})</div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground italic">No subscription records.</p>
                )}
              </div>

              {/* Payment transactions */}
              <div>
                <h3 className="font-semibold mb-1">Payment transactions ({diagnostic.transaction_count})</h3>
                {diagnostic.payment_transactions?.length ? (
                  <div className="space-y-2">
                    {diagnostic.payment_transactions.map((t, i) => (
                      <div key={i} className="bg-secondary/40 rounded p-3 text-xs font-mono space-y-0.5">
                        <div>type: {t.payment_type} · amount: ${t.amount}</div>
                        <div className={t.payment_status === "paid" ? "text-green-600 font-bold" : "text-amber-600"}>
                          status: {t.payment_status}
                        </div>
                        <div>plan_id: {t.plan_id}</div>
                        <div>session_id: {t.session_id}</div>
                        <div>created_at: {t.created_at}</div>
                        {t.paid_at && <div>paid_at: {t.paid_at}</div>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground italic">No payment transactions.</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk clean confirm dialog */}
      <AlertDialog open={confirmBulkClean} onOpenChange={(o) => !bulkCleaning && setConfirmBulkClean(o)}>
        <AlertDialogContent data-testid="bulk-clean-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Clean up all unpaid "subscribers"?</AlertDialogTitle>
            <AlertDialogDescription>
              This will find every user currently flagged as a Founder or Subscriber that
              has <strong>no payment proof in our database</strong> and revoke their status
              in one pass. Customer records are preserved (they stay in your marketing list).
              <span className="block mt-3 text-xs text-muted-foreground">
                Safe: this never touches users with paid Stripe invoices or team accounts.
              </span>
              {bulkCleanResult && (
                <span className="block mt-3 p-2 bg-green-500/10 border border-green-500/30 rounded text-green-700 text-xs">
                  Last run cleaned <strong>{bulkCleanResult.users_cleaned}</strong> user(s).
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkCleaning}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkClean}
              disabled={bulkCleaning}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="confirm-bulk-clean"
            >
              {bulkCleaning ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Cleaning…</>
              ) : (
                <><Wand2 className="h-4 w-4 mr-2" /> Yes, clean them all</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Checkout failures list modal */}
      <Dialog open={showFailuresModal} onOpenChange={setShowFailuresModal}>
        <DialogContent
          className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col"
          data-testid="checkout-failures-modal"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              Failed checkout attempts
            </DialogTitle>
            <DialogDescription>
              People who clicked Subscribe but got a Stripe error. Once Stripe is
              working again, use Admin → Broadcast to send them a rescue email
              with a promo code.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 pb-2 flex-wrap">
            <Button
              onClick={refreshFailures}
              disabled={failuresLoading}
              variant="outline"
              size="sm"
              data-testid="refresh-failures-btn"
            >
              {failuresLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              Refresh
            </Button>
            <Button
              onClick={copyFailedEmails}
              variant="outline"
              size="sm"
              data-testid="modal-copy-emails-btn"
            >
              Copy all emails
            </Button>
            <Button
              onClick={markAllRecovered}
              variant="outline"
              size="sm"
              className="border-green-500 text-green-700 hover:bg-green-50"
              data-testid="mark-recovered-btn"
            >
              Mark all as recovered
            </Button>
          </div>
          <div className="overflow-y-auto -mx-6 px-6 flex-1">
            {failuresData?.rows?.length ? (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                    <th className="py-2 pr-3">Last attempt</th>
                    <th className="py-2 pr-3">Email</th>
                    <th className="py-2 pr-3">Plan</th>
                    <th className="py-2 pr-3 text-right">Tries</th>
                    <th className="py-2 pr-3">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {failuresData.rows.map((r) => (
                    <tr
                      key={r.email}
                      className="border-b last:border-0"
                      data-testid="checkout-failure-row"
                    >
                      <td className="py-2 pr-3 text-xs whitespace-nowrap">
                        {r.last_attempt_at ? new Date(r.last_attempt_at).toLocaleString("en-US", {
                          month: "short", day: "numeric", year: "numeric",
                          hour: "numeric", minute: "2-digit",
                        }) : "—"}
                      </td>
                      <td className="py-2 pr-3 font-mono text-xs">
                        {r.email}
                        {r.name && <div className="text-[11px] text-muted-foreground">{r.name}</div>}
                      </td>
                      <td className="py-2 pr-3 text-xs">{r.plan_id || "—"}</td>
                      <td className="py-2 pr-3 text-right font-mono">{r.attempt_count}</td>
                      <td className="py-2 pr-3 text-xs text-red-700 max-w-[240px] truncate" title={r.error}>
                        {r.error_type || "Error"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-sm text-muted-foreground py-4">
                No failed checkouts on file. 🎉
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
